import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type CacheInvalidationResult,
  type CacheSafetyReport,
  type CloudflarePermission,
  type CloudflareTokenCheck,
  type CloudflareView,
  type CloudflareZoneList,
} from '../api';
import { humanize } from '../labels';
import { CloudflareTokenField } from './CloudflareTokenField';
import { Field, HelpTip, Input, Select, Table } from './primitives';
import { ErrorNotice, StatusBadge } from './ui';

const ZONE_ID = /^[0-9a-f]{8,64}$/i;

/** Whether a string has the shape of a Cloudflare zone identifier. */
export function isZoneId(value: string): boolean {
  return ZONE_ID.test(value.trim());
}

interface CloudflareSetupProps {
  projectId: string;
  applicationId: string;
  environmentId: string;
  /** The zone identifier in the form, saved or not. */
  zoneId: string;
  /** The zone's name once it is known; recorded with the settings. */
  zoneName: string;
  onZone: (id: string, name: string) => void;
  /** The zone identifier that is saved with the component, if its provider is Cloudflare. */
  savedZoneId?: string;
  /** The form differs from what is saved. */
  dirty: boolean;
  saving: boolean;
  /** Saves the cache settings and keeps the dialog open. */
  onSave: () => Promise<void>;
}

/**
 * Cloudflare setup of one component, in the order a person does it (CO-201,
 * phase 3): store the token, pick or enter the zone, check what the token can
 * do, read which hostnames the zone covers and which a purge names, save,
 * test with a real purge, and ask the edge whether it would cache the
 * personalised routes. Every Cloudflare call is a read except the one purge
 * the person asks for; no rule, record or setting is ever written.
 */
export function CloudflareSetup({ projectId, applicationId, environmentId, zoneId, zoneName, onZone, savedZoneId, dirty, saving, onSave }: CloudflareSetupProps) {
  const [tokenStored, setTokenStored] = useState(false);
  const [check, setCheck] = useState<CloudflareTokenCheck | null>(null);
  const [zones, setZones] = useState<CloudflareZoneList | null>(null);
  const [finding, setFinding] = useState(false);
  const [view, setView] = useState<CloudflareView | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purged, setPurged] = useState<CacheInvalidationResult | null>(null);
  const [safety, setSafety] = useState<CacheSafetyReport | null>(null);
  const [safetyHost, setSafetyHost] = useState('');
  const [checkingSafety, setCheckingSafety] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const zoneEntered = isZoneId(zoneId);
  const savedHere = Boolean(savedZoneId) && savedZoneId === zoneId.trim() && !dirty;
  // A check belongs to the zone it was made for; typing another zone retires it.
  const zoneNow = useRef(zoneId.trim());
  zoneNow.current = zoneId.trim();
  const [checkedZone, setCheckedZone] = useState('');
  const onChecked = useCallback((c: CloudflareTokenCheck | null) => {
    setCheck(c);
    setCheckedZone(c ? zoneNow.current : '');
  }, []);
  const onStored = useCallback((stored: boolean) => setTokenStored(stored), []);
  const current = check !== null && checkedZone === zoneId.trim() ? check : null;

  // The zone's own name, once a check reads it, is kept with the settings.
  useEffect(() => {
    const name = current?.zone?.name;
    if (name && current.state === 'valid' && name !== zoneName) onZone(zoneId, name);
  }, [current, zoneId, zoneName, onZone]);

  // A check on a zone that can be read, or whose token is merely too narrow,
  // is followed by the hostnames the zone covers.
  const checkedState = current?.state;
  useEffect(() => {
    setView(null);
    if (!zoneEntered || (checkedState !== 'valid' && checkedState !== 'insufficient_scope')) return;
    let live = true;
    api
      .getComponentCloudflare(projectId, applicationId, environmentId, zoneId.trim())
      .then((v) => live && setView(v))
      .catch((err) => live && setError(err instanceof Error ? err : new Error(String(err))));
    return () => {
      live = false;
    };
  }, [checkedState, zoneEntered, zoneId, projectId, applicationId, environmentId]);

  async function findZones() {
    setFinding(true);
    setError(null);
    try {
      const list = await api.listComponentCloudflareZones(projectId, applicationId, environmentId);
      setZones(list);
      // Only a zone found by name is picked for the person; an entered one stays.
      if (list.suggested_zone_id && !zoneEntered) {
        const pick = list.zones.find((z) => z.id === list.suggested_zone_id);
        if (pick) onZone(pick.id, pick.name);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setFinding(false);
    }
  }

  async function purgeNow() {
    setPurging(true);
    setConfirming(false);
    setError(null);
    setPurged(null);
    try {
      setPurged(await api.purgeComponentCache(projectId, applicationId, environmentId));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setPurging(false);
    }
  }

  async function checkRoutes() {
    setCheckingSafety(true);
    setError(null);
    try {
      setSafety(await api.checkComponentCacheSafety(projectId, applicationId, environmentId, safetyHost ? { hostname: safetyHost } : undefined));
    } catch (err) {
      setSafety(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setCheckingSafety(false);
    }
  }

  const invalidated = view?.hostnames.filter((h) => h.purged).map((h) => h.hostname) ?? [];
  const hostChoices = view?.hostnames.filter((h) => h.covered !== false).map((h) => h.hostname) ?? [];

  return (
    <div className="cloudflare-setup build-form__wide" data-testid="cloudflare-setup">
      <CloudflareTokenField
        help={
          <HelpTip label="What the token needs">
            Required on this zone only: Zone · Zone · Read, Zone · Cache Purge · Purge. Optional: Zone Settings · Read, DNS · Read
          </HelpTip>
        }
        projectId={projectId}
        applicationId={applicationId}
        environmentId={environmentId}
        checkable={zoneEntered}
        zoneId={zoneEntered ? zoneId.trim() : undefined}
        onChecked={onChecked}
        onStored={onStored}
      />

      <div className="cloudflare-setup__zone">
        <Field label="Cloudflare zone" hint={zoneName ? undefined : 'Zone ID, from the zone Overview in Cloudflare'}>
          <Input
            type="text"
            className="input--mono"
            value={zoneId}
            onChange={(e) => onZone(e.target.value, '')}
            placeholder="Zone ID of the storefront’s domain"
            spellCheck={false}
            autoComplete="off"
            data-testid="cache-zone"
          />
        </Field>
        <button type="button" className="button button--secondary button--small" onClick={() => void findZones()} disabled={!tokenStored || finding} title={tokenStored ? undefined : 'Save the token first'} data-testid="cloudflare-find-zones">
          {finding ? 'Finding' : 'Find zones'}
        </button>
      </div>
      {zones ? (
        <div className="cloudflare-setup__zones" data-testid="cloudflare-zones">
          {zones.unreadable ? (
            <span className="muted" data-testid="cloudflare-zones-unreadable">
              {zones.unreadable.startsWith('the token was refused') ? 'Token refused · replace it' : 'Token cannot list zones · enter the ID'}
            </span>
          ) : zones.zones.length === 0 ? (
            <span className="muted">No zones</span>
          ) : (
            <Select
              aria-label="Zones this token can read"
              value={zones.zones.some((z) => z.id === zoneId.trim()) ? zoneId.trim() : ''}
              onChange={(e) => {
                const pick = zones.zones.find((z) => z.id === e.target.value);
                if (pick) onZone(pick.id, pick.name);
              }}
              data-testid="cloudflare-zone-select"
            >
              <option value="" disabled>
                Pick a zone
              </option>
              {zones.zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name} · {humanize(z.status)}
                  {z.covers.length ? ` · covers ${z.covers.length}` : ''}
                  {z.id === zones.suggested_zone_id ? ' · suggested' : ''}
                </option>
              ))}
            </Select>
          )}
          {zones.truncated ? <span className="muted">First {zones.zones.length} zones</span> : null}
        </div>
      ) : null}

      {current?.permissions?.length ? <PermissionsTable permissions={current.permissions} /> : null}
      {view ? <HostnamesTable view={view} /> : null}

      <div className="cloudflare-setup__actions row-actions" data-testid="cloudflare-setup-actions">
        <button type="button" className="button button--secondary button--small" onClick={() => void onSave()} disabled={saving || !zoneEntered || (savedHere && !dirty)} data-testid="cloudflare-save-zone">
          {saving ? 'Saving' : savedHere ? 'Saved' : 'Save zone'}
        </button>
        {confirming ? (
          <>
            <button type="button" className="button button--danger-quiet button--small" onClick={() => void purgeNow()} data-testid="cloudflare-purge-confirm">
              Invalidate {invalidated.length > 0 ? invalidated.join(', ') : 'its hostnames'}
            </button>
            <button type="button" className="button button--secondary button--small" onClick={() => setConfirming(false)} autoFocus>
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            className="button button--secondary button--small"
            onClick={() => setConfirming(true)}
            disabled={!savedHere || purging}
            title={savedHere ? undefined : 'Save the zone first'}
            data-testid="cloudflare-test-purge"
          >
            {purging ? 'Purging' : 'Purge now'}
          </button>
        )}
        {hostChoices.length > 1 ? (
          <Select aria-label="Hostname to check" value={safetyHost} onChange={(e) => setSafetyHost(e.target.value)} data-testid="cloudflare-safety-host">
            <option value="">{hostChoices[0]}</option>
            {hostChoices.slice(1).map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </Select>
        ) : null}
        <button type="button" className="button button--secondary button--small" onClick={() => void checkRoutes()} disabled={checkingSafety} data-testid="cloudflare-check-routes">
          {checkingSafety ? 'Checking' : 'Check cached routes'}
        </button>
      </div>
      {purged ? <PurgeResult result={purged} /> : null}
      {safety ? <SafetyReport report={safety} /> : null}
      <ErrorNotice error={error} />
    </div>
  );
}

function PermissionsTable({ permissions }: { permissions: CloudflarePermission[] }) {
  return (
    <Table label="Token permissions" compact tableTestId="cloudflare-permissions">
      <thead>
        <tr>
          <th scope="col">Permission</th>
          <th scope="col">Needed</th>
          <th scope="col">Found</th>
        </tr>
      </thead>
      <tbody>
        {permissions.map((p) => (
          <tr key={p.key}>
            <th scope="row">{p.name}</th>
            <td>{p.required ? 'Required' : 'Optional'}</td>
            <td>
              <StatusBadge
                kind="cloudflarePermission"
                value={p.state === 'missing' && p.required ? 'missing_required' : p.state}
                title={p.state === 'untestable' ? 'Only a purge shows it. Use Purge now.' : undefined}
                testId={`cloudflare-permission-${p.key}`}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function HostnamesTable({ view }: { view: CloudflareView }) {
  if (view.hostnames.length === 0) return <span className="muted">No hostnames in this environment</span>;
  return (
    <Table label="Hostnames this zone covers" compact tableTestId="cloudflare-hostnames">
      <thead>
        <tr>
          <th scope="col">Hostname</th>
          <th scope="col">Zone</th>
          <th scope="col">Proxied</th>
          <th scope="col">Invalidated</th>
        </tr>
      </thead>
      <tbody>
        {view.hostnames.map((h) => (
          <tr key={h.hostname}>
            <th scope="row">
              <code translate="no">{h.hostname}</code>
            </th>
            <td>{h.covered === undefined ? <span className="muted">Unknown</span> : h.covered ? 'In zone' : <span className="cloudflare-setup__warn">Outside zone</span>}</td>
            <td>{h.covered === false ? '–' : h.proxied === undefined ? <span className="muted">{view.unreadable?.dns_records ? 'Not readable' : 'No record'}</span> : h.proxied ? 'Proxied' : 'DNS only'}</td>
            <td>{h.purged ? 'Yes' : 'No'}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function PurgeResult({ result }: { result: CacheInvalidationResult }) {
  return (
    <div className="cell-inline" data-testid="cloudflare-purge-result">
      {result.skipped ? (
        <span className="badge badge--neutral">Nothing purged</span>
      ) : (
        <span className="badge badge--success">Accepted by Cloudflare</span>
      )}
      <span className="muted">
        {result.hostnames.join(', ')}
        {result.attempts && result.attempts > 1 ? ` · ${result.attempts} attempts` : ''}
        {result.duration_ms ? ` · ${result.duration_ms} ms` : ''}
      </span>
    </div>
  );
}

function probeSummary(route: CacheSafetyReport['routes'][number]): string {
  return route.probes.map((p) => p.cache_status || (p.error ? 'No answer' : p.status ? String(p.status) : '–')).join(' · ');
}

function SafetyReport({ report }: { report: CacheSafetyReport }) {
  return (
    <div className="cloudflare-setup__safety" data-testid="cloudflare-safety">
      <div className="cell-inline">
        <span className="field__label">Cached routes</span>
        <StatusBadge kind="cacheSafety" value={report.verdict} testId="cloudflare-safety-verdict" />
      </div>
      <Table label="Personalised routes" compact tableTestId="cloudflare-safety-routes">
        <thead>
          <tr>
            <th scope="col">Route</th>
            <th scope="col">Result</th>
            <th scope="col">
              Edge <span className="muted">anonymous · again · cookie · header</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {report.routes.map((r) => (
            <tr key={`${r.hostname}${r.path}`}>
              <th scope="row">
                <code translate="no">{r.path}</code>
                <span className="muted"> {r.hostname}</span>
              </th>
              <td>
                <StatusBadge kind="cacheRoute" value={r.verdict} title={r.detail} />
              </td>
              <td>
                <code translate="no">{probeSummary(r)}</code>
                {r.verdict !== 'safe' && r.detail ? <span className="muted cloudflare-setup__detail"> {r.detail}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {report.verdict === 'unsafe' || report.verdict === 'warning' ? (
        <span className="cloudflare-setup__warn" data-testid="cloudflare-safety-warning">
          Bypass these routes in Cloudflare · Commerce Ops changes no rules
        </span>
      ) : null}
    </div>
  );
}
